import { ArrowLeft, FileWarning, Info, TriangleAlert } from "lucide-react"
import * as React from "react"
import { Link, useParams } from "react-router-dom"

import { ConditionTree, statusTone } from "@/components/rules/condition-tree"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { getRuleCondition, type RuleCondition } from "@/lib/api"
import { EMPTY, formatDate, formatText, formatTime } from "@/lib/format"

/**
 * Тип правила → вкладка, с которой оно открывается. Третья строка — наши
 * собственные правила (AMEND-13 §3), и она здесь не для полноты: без неё
 * карточка правила ЧС уводила бы на Custom rules, где его нет.
 */
const RULE_TABS: Record<string, { path: string; label: string }> = {
  default: { path: "/scoring/default", label: "Default rules" },
  custom: { path: "/scoring/custom", label: "Custom rules" },
  local: { path: "/scoring/local", label: "Local rules" },
}

/**
 * Карточка правила — только чтение.
 *
 * Показывает **настоящее** условие из каталога: дерево приходит из
 * `/api/scoring/rules/{id}/condition` тем же, каким по нему считает движок.
 * Если условия нет, карточка не молчит и не рисует пустое место: видно, чем
 * именно отличается «записи в каталоге нет вовсе» от «запись есть, но название
 * правила однозначно не читается», и что по этому поводу сделает движок —
 * его собственными словами.
 *
 * Правка условий здесь не делается: они заводятся файлами в
 * `rules/conditions/` и попадают в базу импортом (AMEND-1 §BLOCK 2).
 */
export function RuleCardPage() {
  const { id } = useParams()
  const ruleId = Number(id)
  const [data, setData] = React.useState<RuleCondition | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [reload, setReload] = React.useState(0)

  React.useEffect(() => {
    if (!Number.isInteger(ruleId) || ruleId < 1) {
      setLoading(false)
      setError(`«${id}» is not a rule id`)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    getRuleCondition(ruleId, controller.signal)
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setData(null)
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [ruleId, id, reload])

  // Вкладка, с которой правило открыли. Тип известен из ответа ручки, а не из
  // адреса: карточка живёт по `/scoring/rules/{id}` для всех трёх вкладок.
  // Пока ответа нет (загрузка, ошибка) — ведём на Custom rules, самую крупную.
  const back = RULE_TABS[data?.rule_type ?? ""] ?? RULE_TABS.custom

  return (
    <div className="p-5">
      <Link
        to={back.path}
        className="mb-3 inline-flex items-center gap-1.5 text-xs text-fg-muted transition-colors duration-150 ease-snap hover:text-fg"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        {back.label}
      </Link>

      {loading && <CardSkeleton />}

      {!loading && error && (
        <section className="animate-panel-in rounded border border-line bg-surface p-8 text-center shadow-panel">
          <p className="text-sm font-medium text-decline">{error}</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
            The card stays empty on purpose — an empty card would read as “this rule has no
            condition”, which is a different thing entirely.
          </p>
          <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
            Retry
          </Button>
        </section>
      )}

      {!loading && !error && data && <RuleCard rule={data} />}
    </div>
  )
}

function RuleCard({ rule }: { rule: RuleCondition }) {
  return (
    <div className="space-y-4">
      <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-fg-muted">{rule.seon_id}</span>
              <Badge tone="accent">{rule.rule_type}</Badge>
              <Badge tone={rule.active ? "approve" : "neutral"}>
                {rule.active ? "on" : "off"}
              </Badge>
            </div>
            <h2 className="mt-1 text-lg font-semibold leading-tight text-fg">
              {formatText(rule.name)}
            </h2>
          </div>
          <div className="text-right">
            <span className="stencil-label block text-fg-subtle">Action</span>
            {rule.score === null ? (
              <span className="text-sm text-decline">{EMPTY}</span>
            ) : (
              <span className="text-xl font-medium text-fg">
                {Number(rule.score) < 0 ? rule.score : `+${rule.score}`}
              </span>
            )}
          </div>
        </header>

        {rule.score_error && (
          <p className="flex items-start gap-2 border-b border-line bg-decline/5 px-4 py-2 text-xs leading-relaxed text-decline">
            <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span>
              The rule score could not be read from the export — {rule.score_error}. The rule is not
              scored at all until that is fixed.
            </span>
          </p>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-4">
          <Meta label="Rule id" value={String(rule.rule_id)} />
          <Meta label="Condition status" value={rule.status ?? "none on file"} />
          <Meta label="Source" value={rule.source ?? EMPTY} mono />
          <Meta
            label="Version"
            value={rule.version === null ? EMPTY : `v${rule.version}`}
            hint={
              rule.version === null
                ? undefined
                : `added ${formatDate(rule.created_at)} ${formatTime(rule.created_at)}`
            }
          />
        </dl>
      </section>

      {rule.condition ? (
        <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
          <header className="flex items-center justify-between border-b border-line px-4 py-3">
            <h3 className="section-title">Condition</h3>
            <span className="text-xs text-fg-subtle">
              v{rule.version} · {rule.source}
            </span>
          </header>
          <div className="px-4 py-4">
            <ConditionTree node={rule.condition} />
          </div>
          {/*
           * Полоса `Reads` — только когда есть что перечислить. У локального
           * правила чёрного списка набор читаемых полей не статичен (движок
           * сверяет со списком поля карточки), ручка отдаёт `fields: []`, и
           * подвал висел голой подписью без единой плашки.
           */}
          {rule.fields.length > 0 && (
            <footer className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-line px-4 py-3">
              <span className="stencil-label text-fg-subtle">Reads</span>
              {rule.fields.map((field) => (
                <span
                  key={field}
                  className="rounded-sm bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-fg-muted"
                >
                  {field}
                </span>
              ))}
            </footer>
          )}
        </section>
      ) : (
        <NoCondition rule={rule} />
      )}

      <p className="flex items-start gap-2 text-xs leading-relaxed text-fg-subtle">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>
          Conditions are kept in the <span className="font-mono">rules/conditions/</span> catalogue
          and loaded by import. This console reads them — it does not create or edit rules.
        </span>
      </p>
    </div>
  )
}

/**
 * Панель «условия нет».
 *
 * Три случая разводятся намеренно: `status: null` — правило никто не разбирал,
 * `no_data` — условие прочитано, а не сходится сторона данных, остальные
 * (`unknown` / `needs_history` / `not_reproducible`) — прочитать само условие не
 * удалось. Под одной формулировкой они выглядели бы одинаково, и неразобранное
 * правило читалось бы как осознанно отложенное.
 *
 * **Почему `no_data` вынесен из «условие не далось» (AMEND-24 §3).** Заголовок
 * «The condition could not be pinned down» стоял на всех 25 записях `no_data`, а
 * у семи из них своя заметка ровно ниже говорит «условие прочитано целиком, но
 * нужна история за 3–5 лет». Заголовок и заметка спорили на одной панели, и
 * владелица читала это как «данных нет» — упрёк стенду, на котором 360 суток и
 * 588 371 строка. Условие у всех 25 как раз прочитано (иначе не было бы известно
 * поле, которого не хватает), поэтому заголовок больше не говорит за данные: что
 * именно коротко — колонка или глубина — называет заметка правила, она
 * единственная знает это по каждому правилу отдельно. Числа покрытия сюда не
 * зашиты намеренно: они меняются с каждой заливкой, их место — индикатор глубины
 * на экране импорта.
 */
function NoCondition({ rule }: { rule: RuleCondition }) {
  const untouched = rule.status === null
  const dataFallsShort = rule.status === "no_data"

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h3 className="section-title">Condition</h3>
        {rule.skip && <Badge tone={statusTone(rule.skip.status)}>{rule.skip.status}</Badge>}
      </header>

      <div className="flex items-start gap-3 px-4 py-4">
        <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-review" strokeWidth={2} />
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-medium text-fg">
            {untouched
              ? "No condition on file for this rule"
              : dataFallsShort
                ? "The condition is read in full — what the data lacks is named below"
                : "The condition could not be pinned down"}
          </p>

          {rule.skip?.reason && (
            <p className="text-xs leading-relaxed text-fg-muted">{rule.skip.reason}</p>
          )}

          {rule.note && rule.note !== rule.skip?.reason && (
            <p className="text-xs leading-relaxed text-fg-muted">{rule.note}</p>
          )}

          {rule.missing.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pt-1">
              <span className="stencil-label text-fg-subtle">Missing</span>
              {rule.missing.map((item) => (
                <span
                  key={item}
                  className="rounded-sm bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-fg-muted"
                >
                  {item}
                </span>
              ))}
            </div>
          )}

          <p className="pt-1 text-xs leading-relaxed text-fg-subtle">
            {untouched
              ? "Nothing is broken and nothing is loading: this rule has not been worked through yet. It is skipped on every replay, and its score is never added."
              : "The rule is skipped on every replay, and its score is never added — a run that includes it is reported as incomplete rather than as a clean zero."}
          </p>
        </div>
      </div>
    </section>
  )
}

function Meta({
  label,
  value,
  mono = false,
  hint,
}: {
  label: string
  value: string
  mono?: boolean
  hint?: string
}) {
  return (
    <div className="min-w-0">
      <dt className="stencil-label text-fg-subtle">{label}</dt>
      <dd className={mono ? "font-mono text-[13px] text-fg" : "text-sm text-fg"}>
        {value}
        {hint && <span className="mt-0.5 block font-sans text-xs text-fg-subtle">{hint}</span>}
      </dd>
    </div>
  )
}

function CardSkeleton() {
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-surface p-4 shadow-panel">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-5 w-64" />
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-8 w-full" />
          ))}
        </div>
      </section>
      <section className="rounded border border-line bg-surface p-4 shadow-panel">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="mt-3 h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-3/4" />
      </section>
    </div>
  )
}
